import { baseURL } from '@/libs/api';
import { clearSavedAuth } from '@/libs/auth-storage';
import { buildSigninPath } from '@/utils/return-to';

const PUBLIC_PATHS = [
  '/api/v2/edi/token',
  '/api/v2/edi/token/refresh',
  '/api/v2/edi/invitation-accept',
  '/api/v2/edi/password-reset',
];
// 取引先を選ぶ前に呼ぶもの(プロフィール)には X-Partner-Id を付けない
const PARTNER_HEADER_SKIP_PATHS = [...PUBLIC_PATHS, '/api/v2/edi/profile'];

let refreshPromise: Promise<string> | null = null;

const isPublicPath = (pathname: string) => PUBLIC_PATHS.some((p) => pathname.startsWith(p));
const isPartnerHeaderSkipPath = (pathname: string) =>
  PARTNER_HEADER_SKIP_PATHS.some((p) => pathname.startsWith(p));

const getHeaders = (pathname: string, headers?: HeadersInit): HeadersInit => {
  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (!isPublicPath(pathname)) {
    const token = localStorage.getItem('accessToken');
    if (token) {
      defaultHeaders.Authorization = `Bearer ${token}`;
    }
  }

  if (!isPartnerHeaderSkipPath(pathname)) {
    const partnerId = localStorage.getItem('partnerId');
    if (partnerId) {
      defaultHeaders['X-Partner-Id'] = partnerId;
    }
  }

  return { ...defaultHeaders, ...headers };
};

const clearAuthAndRedirect = () => {
  clearSavedAuth();
  // 再ログイン後に元の画面へ戻せるよう現在地を引き継ぐ。
  // ここはハードリダイレクトなので react-router の state では持ち回れない。
  window.location.href = buildSigninPath(`${window.location.pathname}${window.location.search}`);
};

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No refresh token');

  const refreshResponse = await fetch(new URL('/api/v2/edi/token/refresh', baseURL), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh: refreshToken }),
  });
  if (!refreshResponse.ok) {
    throw new Error('Token refresh failed');
  }

  const { access } = (await refreshResponse.json()) as { access?: string };
  if (!access) {
    throw new Error('Token refresh failed');
  }
  localStorage.setItem('accessToken', access);
  return access;
};

export const customFetch = async <T>(url: string, options: RequestInit): Promise<T> => {
  const requestUrl = new URL(url, baseURL);
  const fetchWithHeaders = () =>
    fetch(requestUrl, {
      ...options,
      headers: getHeaders(requestUrl.pathname, options.headers),
    });

  let response = await fetchWithHeaders();

  if (response.status === 401 && !isPublicPath(requestUrl.pathname)) {
    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;
    } catch {
      clearAuthAndRedirect();
      throw response;
    }
    response = await fetchWithHeaders();
  }

  if (response.status === 401 && !isPublicPath(requestUrl.pathname)) {
    clearAuthAndRedirect();
    throw response;
  }

  if (!response.ok) {
    throw response;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
};

export type ErrorType<_Error> = Response;
